import { useEffect, useRef } from "react";
import { isSoftwareRendering } from "./render-mode";

/**
 * Céu estrelado do Zênite: 160 pontos com deriva lenta e cintilação, desenhados
 * em canvas. É decoração — nunca carrega informação.
 *
 * Ritmo: a estrela mais rápida anda 5,5 px/s, então a 30 quadros por segundo
 * cada quadro movia menos de 0,2 px — trabalho invisível. O pior é que o canvas
 * cobre a tela, e cada quadro dele obriga o navegador a recompor tudo o que está
 * por cima. Sem aceleração de hardware isso custava 65% de um núcleo parado no
 * Início. A 12 quadros o passo continua abaixo de meio pixel; sem GPU, a 6.
 *
 * Regras herdadas do protótipo e do §15/§22.8:
 * - posições vêm de um gerador determinístico, então o céu é o mesmo a cada
 *   carga e nos testes visuais;
 * - a animação para quando a aba está oculta, quando o canvas sai da tela e
 *   quando o usuário pede movimento reduzido (fica um quadro estático);
 * - as cores vêm dos tokens `--muted` e `--accent`, então acompanham o tema.
 */
interface Star {
  x: number;
  y: number;
  r: number;
  a: number;
  speed: number;
  phase: number;
  tint: boolean;
}

/** Quadros por segundo do céu, com e sem aceleração de hardware. */
const FPS_ACCELERATED = 12;
const FPS_SOFTWARE = 6;

function makeStars(count: number, seed: number): Star[] {
  let state = seed;
  const random = () => (state = (state * 16807) % 2147483647) / 2147483647;
  return Array.from({ length: count }, () => ({
    x: random(),
    y: random(),
    r: 0.4 + random() * 1.1,
    a: 0.35 + random() * 0.5,
    speed: 1.5 + random() * 4,
    phase: random() * Math.PI * 2,
    tint: random() > 0.8,
  }));
}

export function Starfield({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext("2d");
    if (!context) return;

    const stars = makeStars(160, 7);
    if (typeof window.matchMedia !== "function") return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const scheme = window.matchMedia("(prefers-color-scheme: light)");
    let width = 0;
    let height = 0;
    let frame = 0;
    let last = 0;
    let time = 0;
    let visible = true;
    let colors = ["#9a9da8", "#5b8dff"];
    const interval = 1000 / (isSoftwareRendering() ? FPS_SOFTWARE : FPS_ACCELERATED);

    const palette = () => {
      const style = getComputedStyle(document.documentElement);
      const muted = style.getPropertyValue("--muted").trim();
      const accent = style.getPropertyValue("--accent").trim();
      if (muted && accent) colors = [muted, accent];
    };

    const paint = () => {
      if (!width || !height) return;
      context.clearRect(0, 0, width, height);
      for (const star of stars) {
        const x = ((star.x * width + time * star.speed) % (width + 8)) - 4;
        const y =
          ((star.y * height + time * star.speed * 0.24 + Math.sin(time * 0.12 + star.phase) * 3) %
            (height + 8)) -
          4;
        context.globalAlpha = star.a * (0.82 + 0.18 * Math.sin(time * 0.55 + star.phase));
        context.fillStyle = colors[star.tint ? 1 : 0];
        context.beginPath();
        context.arc(x, y, star.r, 0, Math.PI * 2);
        context.fill();
      }
      context.globalAlpha = 1;
    };

    // Com modal aberto o céu fica atrás do véu: parado, ele não obriga o
    // navegador a refazer o desfoque da tela inteira a cada quadro.
    const modalOpen = () => document.documentElement.dataset.modal === "open";
    const paused = () => document.hidden || !visible || motion.matches || modalOpen();

    const tick = (now: number) => {
      frame = 0;
      if (paused()) return;
      if (!last) last = now;
      if (now - last >= interval) {
        time += Math.min((now - last) / 1000, 0.1);
        last = now;
        paint();
      }
      frame = requestAnimationFrame(tick);
    };

    const sync = () => {
      cancelAnimationFrame(frame);
      frame = 0;
      last = 0;
      paint();
      if (!paused()) frame = requestAnimationFrame(tick);
    };

    // A caixa é a do próprio canvas: o CSS decide se ele cobre a entrada
    // (`position: absolute` no pai) ou a viewport inteira (`position: fixed`).
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(0, rect.width);
      height = Math.max(0, rect.height);
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      visible = width > 0 && height > 0;
      palette();
      sync();
    };

    const repaint = () => {
      palette();
      paint();
    };

    const observer =
      typeof ResizeObserver === "function" ? new ResizeObserver(() => resize()) : null;
    if (observer) observer.observe(canvas);
    window.addEventListener("resize", resize);

    const themeObserver = new MutationObserver((records) => {
      if (records.some((record) => record.attributeName === "data-theme")) repaint();
      if (records.some((record) => record.attributeName === "data-modal")) sync();
    });
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme", "data-modal"],
    });

    let intersection: IntersectionObserver | null = null;
    if (typeof IntersectionObserver === "function") {
      intersection = new IntersectionObserver((entries) => {
        visible = entries[0]?.isIntersecting ?? true;
        sync();
      });
      intersection.observe(canvas);
    }

    document.addEventListener("visibilitychange", sync);
    motion.addEventListener("change", sync);
    scheme.addEventListener("change", repaint);
    resize();

    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", resize);
      themeObserver.disconnect();
      intersection?.disconnect();
      document.removeEventListener("visibilitychange", sync);
      motion.removeEventListener("change", sync);
      scheme.removeEventListener("change", repaint);
    };
  }, []);

  return <canvas ref={canvasRef} className={className} aria-hidden="true" />;
}
